/**
 * Движок очков (SPEC §6.1). Награждаем регулярность, а не веса и калории.
 *
 * Чистые функции: одинаковый вход — одинаковый выход. И API, и клиент считают
 * одинаково, поэтому предпросмотр очков на экране совпадает с журналом на сервере.
 */
import { MEALS } from './types.js';
import { dayEndInstant } from './date.js';
export const POINTS = {
    /** За тренировку. */
    workout: 60,
    /** Больше двух тренировок в день не засчитываются. */
    maxWorkoutsPerDay: 2,
    /** За приём пищи, в котором есть хотя бы один продукт. */
    meal: 10,
    /** Завтрак, обед, ужин, перекусы. */
    maxMealsPerDay: 4,
    /** Бонус серии: +5 за каждый день подряд, со 2-го дня. */
    streakStep: 5,
    streakMax: 50,
    /** 2 × 60 + 4 × 10 + 50. */
    dayMax: 210,
    /** Минуты либо подходы — что-то одно из двух. */
    minMinutes: 20,
    minSets: 3,
};
/** Тренировка засчитывается от 20 минут или от 3 подходов. */
export function workoutCounts(durationMin, setsCount) {
    return (durationMin ?? 0) >= POINTS.minMinutes || setsCount >= POINTS.minSets;
}
/**
 * Тренировка, внесённая задним числом больше чем за сутки, в историю идёт,
 * а в зачёт лиги — нет.
 */
export function isLeagueEligible(date, createdAt, timezone) {
    const created = createdAt instanceof Date ? createdAt : new Date(createdAt);
    const deadline = dayEndInstant(date, timezone).getTime() + 24 * 3600 * 1000;
    return created.getTime() <= deadline;
}
/** Бонус серии питания за день: 1 день — 0, 2 — +10, 5 — +25, 10+ — +50. */
export function streakBonus(streakLength) {
    if (streakLength < 2)
        return 0;
    return Math.min(POINTS.streakStep * streakLength, POINTS.streakMax);
}
/** Журнал очков за один день. Сумма никогда не превышает POINTS.dayMax. */
export function scoreDay(input) {
    const drafts = [];
    const counted = input.workouts
        .filter((w) => workoutCounts(w.durationMin, w.setsCount))
        .slice(0, POINTS.maxWorkoutsPerDay);
    for (const workout of counted) {
        drafts.push({
            date: input.date,
            kind: 'workout',
            refId: workout.id,
            points: POINTS.workout,
            gymId: workout.gymId,
            leagueEligible: isLeagueEligible(input.date, workout.createdAt, input.timezone),
        });
    }
    const meals = MEALS.filter((m) => input.mealsWithFood.includes(m)).slice(0, POINTS.maxMealsPerDay);
    for (const meal of meals) {
        drafts.push({
            date: input.date,
            kind: 'meal',
            refId: meal,
            points: POINTS.meal,
            gymId: null,
            leagueEligible: true,
        });
    }
    const bonus = streakBonus(input.streakLength);
    if (bonus > 0) {
        drafts.push({
            date: input.date,
            kind: 'streak',
            refId: null,
            points: bonus,
            gymId: null,
            leagueEligible: true,
        });
    }
    return drafts;
}
export function sumPoints(drafts) {
    return drafts.reduce((acc, d) => acc + d.points, 0);
}
/** Длина серии питания, заканчивающейся датой `date`. */
export function streakLengthFor(date, daysWithFood) {
    if (!daysWithFood.has(date))
        return 0;
    let length = 0;
    let cursor = date;
    while (daysWithFood.has(cursor)) {
        length += 1;
        const [y, m, d] = cursor.split('-').map(Number);
        const prev = new Date(Date.UTC(y, m - 1, d, 12) - 86_400_000);
        cursor = `${prev.getUTCFullYear()}-${String(prev.getUTCMonth() + 1).padStart(2, '0')}-${String(prev.getUTCDate()).padStart(2, '0')}`;
    }
    return length;
}
//# sourceMappingURL=points.js.map